import { Dumbbell, Target, Users2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { TrainingForm } from "@/components/forms/TrainingForm";
import { ASSETS, STAFF } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

const HIGHLIGHTS = [
  { icon: Target, title: "Position-Specific", desc: "Sessions tailored to each player's position, strengths and areas to improve." },
  { icon: Dumbbell, title: "Skill Focused", desc: "Ball-handling, shooting mechanics, footwork, finishing and basketball IQ." },
  { icon: Users2, title: "Private or Small Group", desc: "Train one-on-one or bring a small group of teammates and friends." },
];

export default function PrivateTraining() {
  useSEO({
    title: "Private & Small Group Basketball Training in Norwalk, CT | CT BLAZERS",
    description:
      "Book private and small-group basketball training with CT BLAZERS coaches in Norwalk, CT. Position-specific skill development for youth players ages 10U–17U.",
    keywords: "Basketball Training Norwalk CT, Private Basketball Training Connecticut, CT Blazers training",
    path: "/private-training",
  });

  return (
    <div className="bg-ink-900 min-h-screen">
      <Navbar />
      <PageHeader
        eyebrow="Player Development"
        title="Private &"
        highlight="Small Group Training"
        image={ASSETS.clinicGroup}
        subtitle="CT BLAZERS coaches offer private and small-group basketball training. Choose a coach, tell us your goals, and we'll follow up to build a plan that fits."
      />

      <section className="py-16 sm:py-20 bg-ink-800">
        <div className="container-x">
          <div className="grid gap-6 sm:grid-cols-3">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.08}>
                <div className="hover-lift rounded-2xl border border-white/10 bg-ink-700 p-6 hover:border-blaze-blueLight/50 h-full">
                  <div className="grid h-11 w-11 place-items-center rounded-lg bg-blaze-blue/15 text-blaze-blueLight">
                    <h.icon size={22} />
                  </div>
                  <h3 className="mt-4 font-heading text-2xl tracking-wide text-white uppercase">{h.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{h.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-900">
        <div className="container-x">
          <div className="grid gap-10 lg:grid-cols-5">
            <Reveal className="lg:col-span-2">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-blaze-orange">Coaches Available</span>
                <h2 className="mt-3 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white leading-none">
                  Choose Your Coach
                </h2>
                <p className="mt-4 text-white/65 leading-relaxed">
                  Select a coach in the form to send your inquiry directly. Our staff will confirm availability with you.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {STAFF.filter((s) => s.training).map((c) => (
                    <li key={c.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-ink-700/50 px-4 py-3">
                      <span className="h-2 w-2 rounded-full bg-blaze-orange" />
                      <span className="text-white font-semibold">{c.name}</span>
                      <span className="ml-auto text-xs text-white/50 uppercase tracking-wider">{c.position}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-3">
              <div className="rounded-2xl border border-white/10 bg-ink-700/70 p-6 sm:p-8 shadow-2xl">
                <h3 className="font-heading text-3xl tracking-wide text-white mb-5">Training Inquiry</h3>
                <TrainingForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
